<template>
  <div class="p-6">
    <h2 class="text-lg font-semibold mb-4">🎁 รายการรางวัลที่แจก</h2>

    <!-- ฟอร์มสำหรับสร้างโค้ดใหม่ -->
    <div class="mb-6 border p-4 rounded shadow">
      <h3 class="text-lg font-semibold mb-2">สร้างโค้ดใหม่</h3>
      <form @submit.prevent="createUserGenCode" class="flex flex-col space-y-4">
        <!-- ชื่อผู้รับ -->
        <div>
          <label class="block mb-1">ชื่อผู้รับ</label>
          <input
            v-model="newUsername"
            type="text"
            placeholder="ชื่อผู้รับ"
            class="w-full border px-3 py-2 rounded"
          />
        </div>

        <!-- เลือกรางวัลจาก Dropdown -->
        <div>
          <label class="block mb-1">รางวัล</label>
          <select v-model="selectedRewardId" class="w-full border px-3 py-2 rounded">
            <option value="">-- กรุณาเลือกรางวัล --</option>
            <option v-for="r in rewards" :key="r._id" :value="r._id">
              {{ r.label }} ({{ formatNumber(r.amount) }} เครดิต)
            </option>
          </select>
        </div>

        <button type="submit" class="bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-700">
          สร้างโค้ดใหม่
        </button>
      </form>
    </div>

    <!-- ตารางแสดงรายการโค้ดที่แจก -->
    <table class="w-full table-auto border rounded shadow text-left">
      <thead class="bg-gray-200">
        <tr>
          <th class="px-4 py-2">#</th>
          <th class="px-4 py-2">ชื่อผู้รับ</th>
          <th class="px-4 py-2">รางวัล</th>
          <th class="px-4 py-2">โค้ด</th>
          <th class="px-4 py-2">สถานะ</th>
          <th class="px-4 py-2">วันที่สร้าง</th>
        </tr>
      </thead>
      <tbody>
        <tr v-if="!userGenCodes.length">
          <td class="px-4 py-2" colspan="6">ไม่มีข้อมูล</td>
        </tr>
        <tr
          v-for="(item, i) in paginatedUserGenCodes"
          :key="item._id"
          class="border-b hover:bg-gray-50"
        >
          <td class="px-4 py-2">{{ (currentPage - 1) * itemsPerPage + i + 1 }}</td>
          <td class="px-4 py-2">{{ item.username }}</td>
          <td class="px-4 py-2">
            <span v-if="item.reward">
              {{ item.reward.label }} ({{ formatNumber(item.reward.amount) }} เครดิต)
            </span>
            <span v-else class="text-red-500"> ไม่พบรางวัล </span>
          </td>
          <td class="px-4 py-2 font-mono">{{ item.code }}</td>
          <td class="px-4 py-2">
            <span :class="item.used ? 'text-red-600' : 'text-green-600'">
              {{ item.used ? 'ใช้แล้ว' : 'ยังไม่ใช้' }}
            </span>
          </td>
          <td class="px-4 py-2">{{ formatDate(item.createdAt) }}</td>
        </tr>
      </tbody>
    </table>

    <!-- Pagination Controls -->
    <div class="flex justify-between items-center mt-4">
      <button
        @click="goToPage(currentPage - 1)"
        :disabled="currentPage === 1"
        class="px-3 py-1 bg-gray-300 rounded disabled:opacity-50"
      >
        ⬅️ ก่อนหน้า
      </button>

      <span>หน้า {{ currentPage }} / {{ totalPages }}</span>

      <button
        @click="goToPage(currentPage + 1)"
        :disabled="currentPage === totalPages"
        class="px-3 py-1 bg-gray-300 rounded disabled:opacity-50"
      >
        ถัดไป ➡️
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import axios from 'axios';
import { API_PATHS } from '@/utils/ApiPath.js';
// นำเข้า sweetalert2
import Swal from 'sweetalert2';
import 'sweetalert2/dist/sweetalert2.min.css';

// เก็บรายการ "โค้ด" ที่ได้จาก API
const userGenCodes = ref([]);

// เก็บรายการ "รางวัล" สำหรับ dropdown
const rewards = ref([]);

// สเตตฟอร์มสร้างโค้ดใหม่
const newUsername = ref('');
const selectedRewardId = ref('');

// ฟังก์ชันช่วยในการฟอร์แมตวันที่ (locale ไทย)
const formatDate = (dateString) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleString('th-TH', {
    dateStyle: 'short',
    timeStyle: 'short',
  });
};

// Pagination
const currentPage = ref(1);
const itemsPerPage = 30;

const paginatedUserGenCodes = computed(() => {
  const start = (currentPage.value - 1) * itemsPerPage;
  return userGenCodes.value.slice(start, start + itemsPerPage);
});

const totalPages = computed(() => Math.ceil(userGenCodes.value.length / itemsPerPage));

const goToPage = (page) => {
  if (page >= 1 && page <= totalPages.value) {
    currentPage.value = page;
  }
};

// ฟังก์ชันช่วยในการฟอร์แมตตัวเลข (ใส่คอมมา)
const formatNumber = (value) => {
  if (!value || isNaN(value)) return '0';
  return Number(value).toLocaleString('th-TH');
};

// ดึงข้อมูลรางวัลจาก API
const fetchRewards = async () => {
  try {
    const response = await axios.get(API_PATHS.GET_REWARD);
    rewards.value = response.data;
  } catch (error) {
    console.error('Error fetching rewards:', error);
  }
};

// ดึงข้อมูลโค้ดจาก API
const fetchUserGenCodes = async () => {
  try {
    const response = await axios.get(API_PATHS.GET_USER_GEN_CODE);
    userGenCodes.value = response.data;
  } catch (error) {
    console.error('Error fetching user gen codes:', error);
  }
};

// เรียก fetch ทั้ง "รางวัล" และ "โค้ด" เมื่อคอมโพเนนต์ mount
onMounted(() => {
  fetchRewards();
  fetchUserGenCodes();
});

// ฟังก์ชันสร้างโค้ดใหม่
const createUserGenCode = async () => {
  if (!newUsername.value || !selectedRewardId.value) {
    // ใช้ SweetAlert2 แทน alert()
    Swal.fire({
      icon: 'warning',
      title: 'กรุณากรอกข้อมูลให้ครบถ้วน',
      confirmButtonText: 'ตกลง',
    });
    return;
  }
  try {
    const payload = {
      username: newUsername.value,
      rewardId: selectedRewardId.value,
    };
    await axios.post(API_PATHS.CREATE_USER_GEN_CODE, payload);
    // หลังสร้างสำเร็จ ดึงข้อมูลโค้ดล่าสุดมาแสดง
    await fetchUserGenCodes();
    // ล้างฟอร์ม
    newUsername.value = '';
    selectedRewardId.value = '';
    // แจ้งเตือนด้วย SweetAlert2
    Swal.fire({
      icon: 'success',
      title: 'สร้างโค้ดใหม่สำเร็จ',
      showConfirmButton: false,
      timer: 1500, // ปิดอัตโนมัติหลัง 1.5 วินาที
    });
  } catch (error) {
    console.error('Error creating user gen code:', error);
    // แจ้งเตือนกรณีเกิดข้อผิดพลาด
    Swal.fire({
      icon: 'error',
      title: 'เกิดข้อผิดพลาดในการสร้างโค้ด',
      text: error.response?.data?.message || error.message,
      confirmButtonText: 'ตกลง',
    });
  }
};
</script>
